import { apiRequest } from '@/domains/api';
import type { IFirmListMember } from './firm-lists-contracts';

/**
 * survey-api's firm lists (SR-416 block 3): `/api/v1/admin/firm-lists` — one member put on a
 * list or taken off it, written under `responses.annotate`. The sole caller of that prefix; Team
 * routing's Routing (SR-513) reads the wirehouse list's members through
 * `GET /admin/team-firms/routes` and changes them one firm at a time, so the whole-list read and
 * replace survey-api also offers have no caller here.
 *
 * **An edit pins nothing** (the firm lists design's §6): a participant meets it at their next
 * person lookup or response, a TEAMS registration at its next run; no response moves.
 */

const BASE = '/api/v1/admin/firm-lists';

/** survey-api spells a list's code in `[a-z0-9_]`; anything else is refused before it is spliced
 *  into a path — a `.` or `..` there would reach another route, which no encoding prevents. */
function isFirmListCode(code: string): boolean {
  return /^[a-z0-9_]+$/.test(code);
}

export const firmListsApi = {
  /**
   * One firm put on a list (SR-425's Move to Wires) — 200 with the member. Idempotent: a member
   * is relabelled only when `label` is given; none takes the registry's spelling, and a firm
   * never seen has none (400). An empty 404 is a code survey-api has no list for.
   */
  addMember(code: string, finraFirmId: number, label?: string): Promise<IFirmListMember> {
    if (!isFirmListCode(code)) {
      return Promise.reject(new Error(`“${code}” is not a firm list’s code.`));
    }
    return apiRequest<IFirmListMember>(`${BASE}/${code}/members/${finraFirmId}`, {
      method: 'PUT',
      body: label === undefined ? {} : { label }
    });
  },

  /** One firm taken off a list (SR-425's Move to Gating) — 204, also when it was not on it. */
  async removeMember(code: string, finraFirmId: number): Promise<void> {
    if (!isFirmListCode(code)) throw new Error(`“${code}” is not a firm list’s code.`);
    await apiRequest<null>(`${BASE}/${code}/members/${finraFirmId}`, { method: 'DELETE' });
  }
};
